import { OPS_METRICS_TYPES } from "@amzn/asx-ui-telemetry-client";
import type { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Icon, Text } from "@amzn/storm-ui";
import { exclamationTriangle } from "@amzn/storm-ui-icons";
import { useResizeObserver } from "@react-hookz/web";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import React from "react";
import { Document, pdfjs } from "react-pdf";
import styled from "styled-components";

import {
    CLOUDWATCH_RUM_COUNTERS,
    TELEMETRY_ID,
} from "../../constants/telemetry";
import { asxTelemetryClient, logWithTakt } from "../../utils/telemetry.utils";
import { PdfContainerSkeletonLoader } from "../discovery/insights/insightDetails/layout";

import { PdfSelector } from "./PdfSelector";
import {
    DivAlignContentCenter,
    DivJustifyItemCenter,
} from "./styled.components";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export interface PdfSelectionViewerProps {
    url: string;
    numPages: number;
    setNumPages: (e: any) => void;
    insightType: InsightType;
    identifier: string;
    selectedSlides: Set<number>;
    setSelectedSlides: (selectedslides: Set<number>) => void;
    isCompliant: boolean;
    noData?: JSX.Element;
}

const PdfSelectorContainer = styled.div`
    display: flex;
    flex-flow: column;
    gap: 12px;
`;

const errorMessage = (): JSX.Element => {
    return (
        <DivJustifyItemCenter>
            <Text>
                <Icon type={exclamationTriangle} style={{ color: "red" }} />
                There was an error loading the preview.
                <br />
                You can view the insights offline by using the Download button
                below.
            </Text>
        </DivJustifyItemCenter>
    );
};

export const PdfSelectionViewer: React.FunctionComponent<
    PdfSelectionViewerProps
> = (props: PdfSelectionViewerProps) => {
    const {
        url,
        numPages,
        setNumPages,
        insightType,
        identifier,
        selectedSlides,
        setSelectedSlides,
        isCompliant,
        noData,
    } = props;

    function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
        logWithTakt(TELEMETRY_ID.PREVIEW_SLIDE_COUNT, {
            numPages,
        });
        setNumPages(numPages);
    }

    const maxWidth = 950;

    const widthRef = React.useRef<HTMLDivElement>(null);
    const [width, setWidth] = React.useState(0);
    useResizeObserver(widthRef, (entry) => {
        setWidth(entry.target.clientWidth);
    });

    const logError = () => {
        logWithTakt(TELEMETRY_ID.PREVIEW_ERROR, {
            insightType,
            identifier,
        });
        asxTelemetryClient.publishCounter(
            CLOUDWATCH_RUM_COUNTERS.PDF_PREVIEW_ERROR,
            OPS_METRICS_TYPES.Error,
            1,
            {
                errorMessage: "Error loading PDF preview",
                insightType,
                identifier,
            },
        );
    };

    return (
        <DivAlignContentCenter
            data-testid="pdf-selection-viewer-container"
            ref={widthRef}
        >
            <Document
                file={url}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<PdfContainerSkeletonLoader />}
                data-testid="pdf-selection-viewer-document"
                noData={noData ?? errorMessage}
                error={errorMessage}
                onLoadError={logError}
                onSourceError={logError}
            >
                <PdfSelectorContainer>
                    {Array.from(new Array(numPages), (_el, index) => (
                        <PdfSelector
                            data-testid={`pdf-checkbox-${index}`}
                            key={index}
                            index={index}
                            width={width ? Math.min(width, maxWidth) : maxWidth}
                            selectedSlides={selectedSlides}
                            setSelectedSlides={setSelectedSlides}
                            isCompliant={isCompliant}
                        />
                    ))}
                </PdfSelectorContainer>
            </Document>
        </DivAlignContentCenter>
    );
};
